import type { LimitOrder, Signature, Transfer } from '@sorare/crypto';

type IAuthorizationRequest<T, R> = R & { __typename: T; fingerprint: string };

interface EthereumBankTransfer {
  senderAddress: string;
  receiverAddress: string;
  amount: bigint;
  feeAmount: bigint;
  deadline: string;
  salt: string;
  proxyAddress: string;
  contractAddress: string;
}

interface SolanaBankTransfer {
  senderAddress: string;
  receiverAddress: string;
  amount: bigint;
  feeAmount: bigint;
  nonce: string;
  expirationTimestamp: number;
  originator: string;
  bankProgramAddress: string;
  authority: string;
}

interface BankTransferCondition {
  conditionalAmount: bigint;
  conditionalCurrency: string;
  conditionalNetwork: string;
  conditionalReceiverAddress: string;
  conditionalSalt: string;
}

interface EthereumBankConditionalTransfer
  extends EthereumBankTransfer, BankTransferCondition {}
interface SolanaBankConditionalTransfer
  extends SolanaBankTransfer, BankTransferCondition {}

type StarkexTransferAuthorizationRequest = IAuthorizationRequest<
  'StarkexTransferAuthorizationRequest',
  Transfer
>;

type StarkexLimitOrderAuthorizationRequest = IAuthorizationRequest<
  'StarkexLimitOrderAuthorizationRequest',
  LimitOrder
>;

type MangopayWalletTransferAuthorizationRequest = IAuthorizationRequest<
  'MangopayWalletTransferAuthorizationRequest',
  {
    mangopayWalletId: string;
    operationHash: string;
    currency: string;
    amount: number;
    nonce: number;
  }
>;

type EthereumBankTransferAuthorizationRequest = IAuthorizationRequest<
  'EthereumBankTransferAuthorizationRequest',
  EthereumBankTransfer
>;

type EthereumBankConditionalTransferAuthorizationRequest =
  IAuthorizationRequest<
    'EthereumBankConditionalTransferAuthorizationRequest',
    EthereumBankConditionalTransfer
  >;

type SolanaTokenTransferAuthorizationRequest = IAuthorizationRequest<
  'SolanaTokenTransferAuthorizationRequest',
  {
    expirationTimestamp: number;
    merkleTreeAddress: string;
    transferProxyProgramAddress: string;
    leafIndex: number;
    senderAddress: string;
    receiverAddress: string;
    assetId: string;
    nonce: string;
    originator: string;
  }
>;

type BaseWithdrawalAuthorizationRequest = IAuthorizationRequest<
  'BaseWithdrawalAuthorizationRequest',
  {
    senderAddress: string;
    receiverAddress: string;
    destinationAddress: string;
    amount: bigint;
    feeAmount: bigint;
    deadline: string;
    salt: string;
    proxyAddress: string;
    contractAddress: string;
  }
>;

type OffRampAuthorizationRequest = IAuthorizationRequest<
  'OffRampAuthorizationRequest',
  {
    senderAddress: string;
    receiverAddress: string;
    amount: bigint;
    feeAmount: bigint;
    deadline: string;
    salt: string;
    proxyAddress: string;
    contractAddress: string;
    bridgeRecipient: string;
    amountOutMinimum: string;
    data: string;
  }
>;

type SolanaBankTransferAuthorizationRequest = IAuthorizationRequest<
  'SolanaBankTransferAuthorizationRequest',
  SolanaBankTransfer
>;

type SolanaBankConditionalTransferAuthorizationRequest = IAuthorizationRequest<
  'SolanaBankConditionalTransferAuthorizationRequest',
  SolanaBankConditionalTransfer
>;

type SolanaBankWithdrawalAuthorizationRequest = IAuthorizationRequest<
  'SolanaBankWithdrawalAuthorizationRequest',
  {
    senderAddress: string;
    receiverAddress: string;
    amount: bigint;
    feeAmount: bigint;
    nonce: string;
    expirationTimestamp: number;
    originator: string;
    bankProgramAddress: string;
    authority: string;
  }
>;

export type AuthorizationRequest =
  | StarkexTransferAuthorizationRequest
  | StarkexLimitOrderAuthorizationRequest
  | MangopayWalletTransferAuthorizationRequest
  | EthereumBankTransferAuthorizationRequest
  | SolanaTokenTransferAuthorizationRequest
  | SolanaBankWithdrawalAuthorizationRequest
  | BaseWithdrawalAuthorizationRequest
  | OffRampAuthorizationRequest
  | SolanaBankTransferAuthorizationRequest
  | SolanaBankWithdrawalAuthorizationRequest
  | SolanaBankConditionalTransferAuthorizationRequest
  | EthereumBankConditionalTransferAuthorizationRequest;

export const isAStarkexTransferAuthorizationRequest = (
  request: AuthorizationRequest
): request is StarkexTransferAuthorizationRequest => {
  return request.__typename === 'StarkexTransferAuthorizationRequest';
};

export const isAStarkexLimitOrderAuthorizationRequest = (
  request: AuthorizationRequest
): request is StarkexLimitOrderAuthorizationRequest => {
  return request.__typename === 'StarkexLimitOrderAuthorizationRequest';
};

export const isAMangopayWalletTransferAuthorizationRequest = (
  request: AuthorizationRequest
): request is MangopayWalletTransferAuthorizationRequest => {
  return request.__typename === 'MangopayWalletTransferAuthorizationRequest';
};

export const isAEthereumBankTransferAuthorizationRequest = (
  request: AuthorizationRequest
): request is EthereumBankTransferAuthorizationRequest => {
  return request.__typename === 'EthereumBankTransferAuthorizationRequest';
};

export const isAEthereumBankConditionalTransferAuthorizationRequest = (
  request: AuthorizationRequest
): request is EthereumBankConditionalTransferAuthorizationRequest => {
  return (
    request.__typename === 'EthereumBankConditionalTransferAuthorizationRequest'
  );
};

export const isABaseWithdrawalAuthorizationRequest = (
  request: AuthorizationRequest
): request is BaseWithdrawalAuthorizationRequest => {
  return request.__typename === 'BaseWithdrawalAuthorizationRequest';
};

export const isAOffRampAuthorizationRequest = (
  request: AuthorizationRequest
): request is OffRampAuthorizationRequest => {
  return request.__typename === 'OffRampAuthorizationRequest';
};

export const isASolanaTokenTransferAuthorizationRequest = (
  request: AuthorizationRequest
): request is SolanaTokenTransferAuthorizationRequest => {
  return request.__typename === 'SolanaTokenTransferAuthorizationRequest';
};

export const isASolanaBankTransferAuthorizationRequest = (
  request: AuthorizationRequest
): request is SolanaBankTransferAuthorizationRequest => {
  return request.__typename === 'SolanaBankTransferAuthorizationRequest';
};

export const isASolanaBankConditionalTransferAuthorizationRequest = (
  request: AuthorizationRequest
): request is SolanaBankConditionalTransferAuthorizationRequest => {
  return (
    request.__typename === 'SolanaBankConditionalTransferAuthorizationRequest'
  );
};

export const isASolanaBankWithdrawalAuthorizationRequest = (
  request: AuthorizationRequest
): request is SolanaBankWithdrawalAuthorizationRequest => {
  return request.__typename === 'SolanaBankWithdrawalAuthorizationRequest';
};

type IAuthorizationApproval = {
  fingerprint: string;
};

type StarkexAuthorizationApproval = {
  nonce: number;
  expirationTimestamp: number;
  signature: Signature;
};

type StarkexTransferAuthorizationApproval = IAuthorizationApproval & {
  starkexTransferApproval: StarkexAuthorizationApproval;
};

type StarkexLimitOrderAuthorizationApproval = IAuthorizationApproval & {
  starkexLimitOrderApproval: StarkexAuthorizationApproval;
};

type MangopayWalletTransferAuthorizationApproval = IAuthorizationApproval & {
  mangopayWalletTransferApproval: {
    nonce: number;
    signature: Signature;
  };
};

type EthereumBankTransferAuthorizationApproval = IAuthorizationApproval & {
  ethereumBankTransferApproval: {
    signature: string;
    deadline: string;
    salt: string;
  };
};

type EthereumBankConditionalTransferAuthorizationApproval =
  IAuthorizationApproval & {
    ethereumBankTransferApproval: {
      signature: string;
      deadline: string;
      salt: string;
      conditionalSalt: string;
    };
  };

type SolanaTokenTransferAuthorizationApproval = IAuthorizationApproval & {
  solanaTokenTransferApproval: {
    nonce: string;
    signature: string;
    expirationTimestamp: number;
  };
};

type SolanaBankTransferAuthorizationApproval = IAuthorizationApproval & {
  solanaBankTransferApproval: {
    nonce: string;
    signature: string;
    expirationTimestamp: number;
  };
};

type SolanaBankConditionalTransferAuthorizationApproval =
  IAuthorizationApproval & {
    solanaBankTransferApproval: {
      nonce: string;
      signature: string;
      expirationTimestamp: number;
      conditionalSalt: string;
    };
  };

type SolanaBankWithdrawalAuthorizationApproval = IAuthorizationApproval & {
  solanaBankWithdrawalApproval: {
    signature: string;
  };
};

export type AuthorizationApproval =
  | StarkexTransferAuthorizationApproval
  | StarkexLimitOrderAuthorizationApproval
  | MangopayWalletTransferAuthorizationApproval
  | EthereumBankTransferAuthorizationApproval
  | EthereumBankConditionalTransferAuthorizationApproval
  | SolanaTokenTransferAuthorizationApproval
  | SolanaBankTransferAuthorizationApproval
  | SolanaBankConditionalTransferAuthorizationApproval
  | SolanaBankWithdrawalAuthorizationApproval;
